import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, Typography } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { AiOutlinePlus } from 'react-icons/ai';

import { ConfirmDialog } from '@vyce/core/src/components';
import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { DeviceContext } from '@vyce/core/src/contexts';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { SafeDndProvider } from '@vyce/core/src/components/SafeDndProvider';
import { useDebounceValue } from '@vyce/core/src/hooks';
import { getPaddingForContent } from '@vyce/core/src/utils';
import {
  buildFormUrlSegment,
  formTitleSlugToDisplayName,
  parseFormUrlSegment,
} from '@vyce/core/src/utils/formUrl';
import {
  FormFolderDoc,
  FormTemplateDoc,
  FormTemplateFolderDoc,
} from '@vyce/core/src/api/types';
import { User } from '@vyce/core/src/types';
import {
  addFormTemplateToFolderRequest,
  archiveFormTemplateFolderRequest,
  archiveFormTemplateRequest,
  createFormTemplateFolderRequest,
  deleteFormTemplateFolderRequest,
  deleteFormTemplateRequest,
  duplicateFormTemplateRequest,
  getFormTemplateFoldersRequest,
  getFormTemplatesRequest,
  moveFormTemplateFolderRequest,
  updateFormTemplateFolderRequest,
} from '@vyce/core/src/api/forms';

import { useFormTemplates } from './hooks/useFormTemplates';
import { TemplatesDataGrid } from './components/TemplatesDataGrid';
import { FolderCard } from './components/FolderCard';
import { FolderCardSkeleton } from './components/FolderCardSkeleton';
import { CreateFolderDialog } from './components/CreateFolderDialog';
import { ChangeFolderDialog } from './components/ChangeFolderDialog';
import { MoveFolderDialog } from './components/MoveFolderDialog';
import { RenameFolderDialog } from './components/RenameFolderDialog';

const TEMPLATE_DRAG_TYPE = 'TEMPLATE_UNASSIGNED_CARD';
const FOLDERS_FETCH_LIMIT = 500;
const SEARCH_DEBOUNCE_MS = 400;

export const TemplateFolderPage: React.FC<{ user: User; companyId: string }> = ({ user, companyId }) => {
  const { folderSlug } = useParams<{ folderSlug: string }>();
  const history = useHistory();
  const { isMobile } = useContext(DeviceContext);
  const { showNotification, handleServerError } = useContext(NotificationContext);
  const showNotificationRef = useRef(showNotification);
  const handleServerErrorRef = useRef(handleServerError);
  const padding = getPaddingForContent(isMobile);

  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [folders, setFolders] = useState<FormTemplateFolderDoc[]>([]);
  const [templates, setTemplates] = useState<FormTemplateDoc[]>([]);
  const [total, setTotal] = useState(0);
  const [folderSearch, setFolderSearch] = useState('');
  const debouncedFolderSearch = useDebounceValue(folderSearch, SEARCH_DEBOUNCE_MS);
  const folderSearchForApi = debouncedFolderSearch === null ? '' : debouncedFolderSearch.trim();
  const [templatesSearch, setTemplatesSearch] = useState('');
  const debouncedTemplatesSearch = useDebounceValue(templatesSearch, SEARCH_DEBOUNCE_MS);
  const templatesSearchForApi = debouncedTemplatesSearch === null ? '' : debouncedTemplatesSearch.trim();

  const folderPathSegments = useMemo(() => {
    if (!folderSlug) return [];
    return String(folderSlug)
      .split('/')
      .map(segment => segment.trim())
      .filter(Boolean);
  }, [folderSlug]);

  const currentFolderSlug = folderPathSegments[folderPathSegments.length - 1] || '';
  const parsedFolder = useMemo(() => parseFormUrlSegment(currentFolderSlug), [currentFolderSlug]);
  const folderId = parsedFolder.id;
  const folderTitleFromPath = useMemo(() => {
    if (!parsedFolder.titleSlug) return '';
    return formTitleSlugToDisplayName(parsedFolder.titleSlug);
  }, [parsedFolder]);
  const parentFolderName = useMemo(() => {
    if (folderPathSegments.length < 2) return 'Root';
    const parentSegment = folderPathSegments[folderPathSegments.length - 2];
    const parsedParent = parseFormUrlSegment(parentSegment);
    if (!parsedParent.titleSlug) return 'Root';
    return formTitleSlugToDisplayName(parsedParent.titleSlug);
  }, [folderPathSegments]);

  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  const [openDeleteFolderDialog, setOpenDeleteFolderDialog] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<FormTemplateFolderDoc | null>(null);
  const [deletingFolder, setDeletingFolder] = useState(false);

  const [openMoveFolderDialog, setOpenMoveFolderDialog] = useState(false);
  const [movingFolder, setMovingFolder] = useState(false);

  const [openRenameFolderDialog, setOpenRenameFolderDialog] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(false);

  const [openDeleteTemplateDialog, setOpenDeleteTemplateDialog] = useState(false);
  const [openArchiveTemplateDialog, setOpenArchiveTemplateDialog] = useState(false);
  const [openChangeFolderDialog, setOpenChangeFolderDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplateDoc | null>(null);
  const [loadingDeleteTemplate, setLoadingDeleteTemplate] = useState(false);
  const [loadingArchiveTemplate, setLoadingArchiveTemplate] = useState(false);
  const [loadingChangeFolder, setLoadingChangeFolder] = useState(false);
  const [duplicatingTemplateId, setDuplicatingTemplateId] = useState<string | null>(null);
  const [fillingOutTemplateId, setFillingOutTemplateId] = useState<string | null>(null);
  const [fillingInTemplateId, setFillingInTemplateId] = useState<string | null>(null);

  const { createFormFromTemplate, createFormFromTemplateForFillIn } = useFormTemplates({
    user,
    companyId,
  });

  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);

  useEffect(() => {
    handleServerErrorRef.current = handleServerError;
  }, [handleServerError]);

  const loadFolders = useCallback(async () => {
    if (!companyId || !folderId) return;
    setLoadingFolders(true);
    try {
      const foldersResponse = await getFormTemplateFoldersRequest({
        companyId,
        userId: user.uuid,
        parentId: folderId,
        limit: FOLDERS_FETCH_LIMIT,
        search: folderSearchForApi || null,
      });
      setFolders(foldersResponse.folders);
    } catch (error) {
      handleServerErrorRef.current(error);
    } finally {
      setLoadingFolders(false);
    }
  }, [companyId, folderId, user.uuid, folderSearchForApi]);

  const loadTemplates = useCallback(async () => {
    if (!companyId || !folderId) return;
    setLoadingTemplates(true);
    try {
      const templatesResponse = await getFormTemplatesRequest({
        companyId,
        userId: user.uuid,
        offset: 0,
        limit: 100,
        folderId,
        search: templatesSearchForApi || undefined,
      });
      setTemplates(templatesResponse.templates || []);
      setTotal(templatesResponse.total || 0);
    } catch (error) {
      handleServerErrorRef.current(error);
    } finally {
      setLoadingTemplates(false);
    }
  }, [companyId, folderId, user.uuid, templatesSearchForApi]);

  const loadFolderPage = useCallback(async () => {
    await Promise.all([loadFolders(), loadTemplates()]);
  }, [loadFolders, loadTemplates]);

  useEffect(() => {
    void loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const handleDropToSubfolder = useCallback(
    async (item: FormTemplateDoc, targetFolderId: string) => {
      if (!item.id || !targetFolderId) return;
      const previousTemplates = [...templates];
      setTemplates(prev => prev.filter(t => t.id !== item.id));
      try {
        await addFormTemplateToFolderRequest({
          companyId,
          templateId: item.id,
          folderId: targetFolderId,
          userId: user.uuid,
        });
        showNotificationRef.current({
          message: 'Template moved to another folder',
          options: { variant: 'success' },
        });
        await loadFolderPage();
      } catch (error) {
        setTemplates(previousTemplates);
        handleServerErrorRef.current(error);
      }
    },
    [companyId, templates, loadFolderPage, user.uuid]
  );

  const handleFillIn = useCallback(
    async (template: FormTemplateDoc) => {
      if (!template.id) return;
      if (!user?.email && !user?.phone) {
        showNotificationRef.current({
          message: 'Please add email or phone to your profile to use Fill in',
          options: { variant: 'error' },
        });
        return;
      }
      setFillingInTemplateId(template.id);
      const formId = await createFormFromTemplateForFillIn(template.id);
      setFillingInTemplateId(null);
      if (formId) {
        history.push(`/forms/form-to-answer/${buildFormUrlSegment(template.title, formId)}`);
      }
    },
    [createFormFromTemplateForFillIn, history, user?.email, user?.phone]
  );

  const handleSend = useCallback(
    async (template: FormTemplateDoc) => {
      if (!template.id) return;
      setFillingOutTemplateId(template.id);
      const formId = await createFormFromTemplate(template.id);
      setFillingOutTemplateId(null);
      if (formId) {
        history.push(`/forms/my-forms/${buildFormUrlSegment(template.title, formId)}`);
      }
    },
    [createFormFromTemplate, history]
  );

  const handleEditTemplate = useCallback(
    (template: FormTemplateDoc) => {
      if (!template.id) return;
      history.push(`/forms/form-templates/${buildFormUrlSegment(template.title, template.id)}/edit`);
    },
    [history]
  );

  const handleDuplicate = useCallback(
    async (template: FormTemplateDoc) => {
      if (!template.id) return;
      setDuplicatingTemplateId(template.id);
      try {
        await duplicateFormTemplateRequest({
          companyId,
          templateId: template.id,
          userId: user.uuid,
          userName: [user?.first_name, user?.last_name].filter(Boolean).join(' ') || undefined,
        });
        showNotificationRef.current({
          message: 'Template duplicated',
          options: { variant: 'success' },
        });
        await loadFolderPage();
      } catch (error) {
        handleServerErrorRef.current(error);
      } finally {
        setDuplicatingTemplateId(null);
      }
    },
    [companyId, loadFolderPage, user.uuid, user?.first_name, user?.last_name]
  );

  return (
    <Box padding={padding}>
      <Box marginBottom={2} display="flex" justifyContent="space-between" alignItems="center" gridGap={16}>
        <Box display="flex" alignItems="center" gridGap={16} flexGrow={1} minWidth={0}>
          <Typography variant="h6">
            {folderTitleFromPath || parentFolderName}
          </Typography>

          <AppSearchInput
            placeholder="Search folders..."
            value={folderSearch}
            onChange={e => setFolderSearch(e.target.value)}
            isSmall
            smallRounding
            isBorder
            expanded={true}
          />
        </Box>

        <Box display="flex" alignItems="center" gridGap={16}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            style={{ width: '180px' }}
            startIcon={<AiOutlinePlus />}
            onClick={() => history.push('/forms/new-template')}
          >
            Create new template
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setOpenCreateFolderDialog(true)}
          >
            Create subfolder
          </Button>
        </Box>
      </Box>

      <SafeDndProvider backend={HTML5Backend}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
          gridGap={16}
          marginBottom={3}
        >
          {loadingFolders
            ? Array.from({ length: 3 }, (_, i) => (
                <FolderCardSkeleton key={`child-folder-skeleton-${i}`} />
              ))
            : folders.map(folder => {
                const folderAsFormFolder = {
                  ...folder,
                  formsCount: folder.templatesCount,
                } as unknown as FormFolderDoc;
                return (
                  <FolderCard
                    key={folder.id}
                    folder={folderAsFormFolder}
                    dragType={TEMPLATE_DRAG_TYPE}
                    countLabel="Templates"
                    onDrop={(item, targetFolderId) =>
                      handleDropToSubfolder(item as FormTemplateDoc, targetFolderId)
                    }
                    onDeleteFolder={childId => {
                      setSelectedFolderId(childId);
                      setOpenDeleteFolderDialog(true);
                    }}
                    onMoveFolderToFolder={async (sourceFolderId, targetFolderId) => {
                      setLoadingFolders(true);
                      try {
                        await moveFormTemplateFolderRequest({
                          companyId,
                          folderId: sourceFolderId,
                          newParentId: targetFolderId,
                          userId: user.uuid,
                        });
                        showNotificationRef.current({
                          message: 'Folder moved',
                          options: { variant: 'success' },
                        });
                        await loadFolderPage();
                      } catch (error) {
                        handleServerErrorRef.current(error);
                        setLoadingFolders(false);
                      }
                    }}
                    onMoveFolder={target => {
                      setSelectedFolder(target as unknown as FormTemplateFolderDoc);
                      setOpenMoveFolderDialog(true);
                    }}
                    onRenameFolder={target => {
                      setSelectedFolder(target as unknown as FormTemplateFolderDoc);
                      setOpenRenameFolderDialog(true);
                    }}
                    onArchiveFolder={async target => {
                      try {
                        await archiveFormTemplateFolderRequest({
                          companyId,
                          folderId: target.id,
                          userId: user.uuid,
                          archived: target.archived !== true,
                        });
                        showNotificationRef.current({
                          message:
                            target.archived !== true ? 'Folder archived' : 'Folder unarchived',
                          options: { variant: 'success' },
                        });
                        await loadFolderPage();
                      } catch (error) {
                        handleServerErrorRef.current(error);
                      }
                    }}
                    onColourFolder={async (childId, colour) => {
                      const previousColour = folders.find(f => f.id === childId)?.colour ?? null;
                      setFolders(prev =>
                        prev.map(f => (f.id === childId ? { ...f, colour } : f))
                      );
                      try {
                        await updateFormTemplateFolderRequest({
                          companyId,
                          folderId: childId,
                          userId: user.uuid,
                          colour,
                        });
                      } catch (error) {
                        setFolders(prev =>
                          prev.map(f =>
                            f.id === childId ? { ...f, colour: previousColour } : f
                          )
                        );
                        handleServerErrorRef.current(error);
                      }
                    }}
                    onOpenFolder={() =>
                      history.push(
                        `/forms/form-templates/folders/${[
                          ...folderPathSegments,
                          buildFormUrlSegment(folder.name, folder.id),
                        ].join('/')}`
                      )
                    }
                  />
                );
              })}
          {!loadingFolders && folders.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              No Folders
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gridGap={16} marginBottom={1}>
          <Typography variant="h6">
            Templates in folder ({total})
          </Typography>
          <AppSearchInput
            placeholder="Search templates..."
            value={templatesSearch}
            onChange={e => setTemplatesSearch(e.target.value)}
            isSmall
            smallRounding
            isBorder
            expanded={true}
          />
        </Box>

        <TemplatesDataGrid
          rows={templates}
          enableDragAndDrop
          dragType={TEMPLATE_DRAG_TYPE}
          withActions
          fillingInTemplateId={fillingInTemplateId}
          fillingOutTemplateId={fillingOutTemplateId}
          duplicatingTemplateId={duplicatingTemplateId}
          onFillIn={handleFillIn}
          onSend={handleSend}
          onEditTemplate={handleEditTemplate}
          onDuplicateTemplate={handleDuplicate}
          onArchiveTemplate={template => {
            setSelectedTemplate(template);
            setOpenArchiveTemplateDialog(true);
          }}
          onChangeFolder={template => {
            setSelectedTemplate(template);
            setOpenChangeFolderDialog(true);
          }}
          onDeleteTemplate={template => {
            setSelectedTemplate(template);
            setOpenDeleteTemplateDialog(true);
          }}
          height="60vh"
          loading={loadingTemplates}
          total={total}
          setOffset={() => {}}
        />
      </SafeDndProvider>

      <CreateFolderDialog
        open={openCreateFolderDialog}
        folderName={newFolderName}
        loading={creatingFolder}
        onFolderNameChange={setNewFolderName}
        onClose={() => setOpenCreateFolderDialog(false)}
        onCreate={async colour => {
          if (!newFolderName.trim()) return;
          try {
            setCreatingFolder(true);
            await createFormTemplateFolderRequest({
              companyId,
              userId: user.uuid,
              name: newFolderName.trim(),
              parentId: folderId,
              colour,
            });
            setNewFolderName('');
            setOpenCreateFolderDialog(false);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setCreatingFolder(false);
          }
        }}
      />

      <ConfirmDialog
        open={openDeleteTemplateDialog}
        handleClose={() => {
          setOpenDeleteTemplateDialog(false);
          setSelectedTemplate(null);
        }}
        handleConfirm={async () => {
          if (!selectedTemplate?.id) return;
          try {
            setLoadingDeleteTemplate(true);
            await deleteFormTemplateRequest({
              companyId,
              templateId: selectedTemplate.id,
              userId: user.uuid,
            });
            setOpenDeleteTemplateDialog(false);
            setSelectedTemplate(null);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setLoadingDeleteTemplate(false);
          }
        }}
        title="Are you sure you want to delete this template?"
        loading={loadingDeleteTemplate}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <ConfirmDialog
        open={openArchiveTemplateDialog}
        handleClose={() => {
          setOpenArchiveTemplateDialog(false);
          setSelectedTemplate(null);
        }}
        handleConfirm={async () => {
          if (!selectedTemplate?.id) return;
          try {
            setLoadingArchiveTemplate(true);
            await archiveFormTemplateRequest({
              companyId,
              templateId: selectedTemplate.id,
              userId: user.uuid,
              archived: selectedTemplate.archived !== true,
            });
            showNotificationRef.current({
              message:
                selectedTemplate.archived !== true ? 'Template archived' : 'Template unarchived',
              options: { variant: 'success' },
            });
            setOpenArchiveTemplateDialog(false);
            setSelectedTemplate(null);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setLoadingArchiveTemplate(false);
          }
        }}
        title={
          selectedTemplate?.archived
            ? 'Are you sure you want to unarchive this template?'
            : 'Are you sure you want to archive this template?'
        }
        confirmText={selectedTemplate?.archived ? 'Unarchive' : 'Archive'}
        cancelText="Cancel"
        loading={loadingArchiveTemplate}
      />

      <ConfirmDialog
        open={openDeleteFolderDialog}
        handleClose={() => {
          setOpenDeleteFolderDialog(false);
          setSelectedFolderId(null);
        }}
        handleConfirm={async () => {
          if (!selectedFolderId) return;
          try {
            setDeletingFolder(true);
            await deleteFormTemplateFolderRequest({
              companyId,
              folderId: selectedFolderId,
              userId: user.uuid,
              mode: 'cascade',
            });
            setOpenDeleteFolderDialog(false);
            setSelectedFolderId(null);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setDeletingFolder(false);
          }
        }}
        title="Are you sure you want to delete this folder?"
        subtitle="All nested folders inside this folder will also be deleted."
        loading={deletingFolder}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <MoveFolderDialog
        open={openMoveFolderDialog}
        loading={movingFolder}
        companyId={companyId}
        userId={user.uuid}
        kind="template"
        folderId={selectedFolder?.id ?? null}
        currentParentId={selectedFolder?.parentId ?? null}
        onClose={() => {
          setOpenMoveFolderDialog(false);
          setSelectedFolder(null);
        }}
        onConfirm={async newParentId => {
          if (!selectedFolder) return;
          try {
            setMovingFolder(true);
            await moveFormTemplateFolderRequest({
              companyId,
              folderId: selectedFolder.id,
              newParentId,
              userId: user.uuid,
            });
            showNotificationRef.current({
              message: 'Folder moved',
              options: { variant: 'success' },
            });
            setOpenMoveFolderDialog(false);
            setSelectedFolder(null);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setMovingFolder(false);
          }
        }}
      />

      <RenameFolderDialog
        open={openRenameFolderDialog}
        initialName={selectedFolder?.name ?? ''}
        loading={renamingFolder}
        onClose={() => {
          setOpenRenameFolderDialog(false);
          setSelectedFolder(null);
        }}
        onConfirm={async name => {
          if (!selectedFolder) return;
          try {
            setRenamingFolder(true);
            await updateFormTemplateFolderRequest({
              companyId,
              folderId: selectedFolder.id,
              userId: user.uuid,
              name,
            });
            showNotificationRef.current({
              message: 'Folder renamed',
              options: { variant: 'success' },
            });
            setOpenRenameFolderDialog(false);
            setSelectedFolder(null);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setRenamingFolder(false);
          }
        }}
      />

      <ChangeFolderDialog
        open={openChangeFolderDialog}
        loading={loadingChangeFolder}
        companyId={companyId}
        userId={user.uuid}
        kind="template"
        onClose={() => {
          setOpenChangeFolderDialog(false);
          setSelectedTemplate(null);
        }}
        onConfirm={async targetFolderId => {
          if (!selectedTemplate?.id || !targetFolderId) return;
          try {
            setLoadingChangeFolder(true);
            await addFormTemplateToFolderRequest({
              companyId,
              templateId: selectedTemplate.id,
              folderId: targetFolderId,
              userId: user.uuid,
            });
            showNotificationRef.current({
              message: 'Template moved to another folder',
              options: { variant: 'success' },
            });
            setOpenChangeFolderDialog(false);
            setSelectedTemplate(null);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setLoadingChangeFolder(false);
          }
        }}
      />
    </Box>
  );
};
